import { createArticlesQuery } from "@/abilities/mechanic/graphql/articles.query";
import { generateDynamicSearchArticlesOnlyAggregationsQuery } from "@/abilities/mechanic/helpers/article-search-helper";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { convertAggregationsResult } from "@/shared/aggregations/aggregations-resolver";
import { loadMore } from "@/shared/components/scroll/lazy-loading-fetch";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";
import {
    QDataSource,
    QIT_PREVIEW_IMAGE_COLUMN,
    QIT_PREVIEW_IMAGE_ROW_FIELD,
    QTableRowType,
} from "@/shared/components/table/q-data-source";
import { useDataDisplayConfig } from "@/shared/data-display-config/composable/data-display-config";
import { getDataDisplayValue } from "@/shared/data-display-config/composable/data-display.helper";
import { DataDisplayConfigId } from "@/shared/data-display-config/composable/data-display.model";
import { isLinkDisplayField } from "@/shared/data-display-config/data-display.utils.ts";
import { INIT_LOAD_AMOUNT, LAZY_LOAD_AMOUNT } from "@/shared/environment/consts.ts";
import { getSortEntries } from "@/shared/environment/filter/filter-types";
import { generateOrGroupsByFacetsSelected } from "@/shared/facets/facets-filter-generator";
import { Facet, FacetSelected } from "@/shared/facets/facets.model";
import { localizeSearchFields } from "@/shared/search/localize-search-fields";
import { DataSearchConfigId, useSearchConfig } from "@/shared/search/search-config";
import { generateSearchFieldsFilterClauseRegex } from "@/shared/search/search-fields-filter-clause";
import { SearchContext } from "@/shared/search/search.types";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import { ArticleEdge, ArticleFilter } from "@/shared/services/graphql/generated/consumer-graph-types";
import { useLanguageStore } from "@/shared/store/language-store";
import { useQuery } from "@vue/apollo-composable";
import { QTableProps } from "quasar";
import { computed, ComputedRef, ref, Ref } from "vue";
import { useI18n } from "vue-i18n";

export const ADD_TO_COLLECTION_COL_NAME = "addToCollection";

export const useArticlesDataSource = (
    searchContext: Ref<SearchContext>,
    selected: Ref<FacetSelected[]>,
    sort: Ref<SortEntry | undefined>,
    facet: Ref<Facet | undefined>,
    blobId: Ref<string | undefined>
) => {
    const { t } = useI18n();
    const { hasAccess } = useHasAccess({ featureID: AccessFeature.spare_parts });
    const languageStore = useLanguageStore();

    const { result: articleSearchConfigResult } = useSearchConfig(DataSearchConfigId.article);

    const generateArticleFilterVariables = (
        searchContext: SearchContext,
        selected: FacetSelected[],
        sort: SortEntry | undefined,
        blob: string | undefined
    ) => {
        const searchPhrase = searchContext.text ?? "";
        const filteredSearchFields = localizeSearchFields(
            articleSearchConfigResult.value?.searchEntries ?? [],
            languageStore.dataQueryLanguages
        );
        const searchFilterClause = generateSearchFieldsFilterClauseRegex(searchPhrase, filteredSearchFields, true);

        let articleFilter: ArticleFilter = {};

        //The wildcard search with * behaves like empty-string search, otherwise the regex will not fit and no results will be returned (see QIT-2531).
        if (searchContext.text && searchContext.text !== "*") {
            articleFilter = {
                orGroup: [
                    {
                        regex: {
                            articleNumber: `/${searchContext.text}.*/i`,
                        },
                        boost: 10,
                    },
                    ...(searchFilterClause.orGroup ?? []),
                ],
            };
        }
        const copySelected = [...selected];
        const orGroups = generateOrGroupsByFacetsSelected(copySelected);

        if (orGroups.length) articleFilter.andGroup = orGroups;

        if (blob)
            articleFilter.image = {
                blobId: blob,
            };

        const sortEntries = getSortEntries(sort, languageStore.dataQueryLanguages);

        return {
            filter: articleFilter,
            first: INIT_LOAD_AMOUNT,
            acceptedLanguages: languageStore.dataQueryLanguages,
            sort: sortEntries,
        };
    };

    const filter = computed(() => {
        return generateArticleFilterVariables(searchContext.value, selected.value, sort.value, blobId.value);
    });

    const {
        result: dataDisplayResult,
        loading: dataConfigLoading,
        error: dataConfigError,
        priorityDataDisplayFields,
    } = useDataDisplayConfig(DataDisplayConfigId.articleSelection, t);

    const queryDocument = computed(() => {
        if (dataDisplayResult.value)
            return createArticlesQuery({
                datafieldsFragment: dataDisplayResult.value.datafieldsFragment.datafields,
                teaserDatafieldsFragment: dataDisplayResult.value.datafieldsFragment.localizationDatafields,
                withPreviewImages: true,
            });

        return createArticlesQuery({});
    });

    const {
        loading: articlesLoading,
        error: articlesError,
        fetchMore,
        result: articlesResult,
    } = useQuery(
        queryDocument,
        filter,
        computed(() => ({
            enabled: !!(dataDisplayResult.value && sort.value),
        }))
    );

    const hasMoreResults = ref(true);

    function loadMoreArticles() {
        if (!articlesResult.value) {
            return;
        }
        const lastArticle = articlesResult.value.articles.articles[articlesResult.value.articles.articles.length - 1];
        if (!lastArticle || !lastArticle.cursor) {
            return;
        }
        loadMore(fetchMore, lastArticle.cursor, LAZY_LOAD_AMOUNT).then((data) => {
            hasMoreResults.value = data?.data.articles.articles.length === LAZY_LOAD_AMOUNT;
        });
    }

    const articles = computed(() => {
        return articlesResult.value?.articles?.articles ?? [];
    });

    const total = computed(() => {
        return articlesResult.value?.articles?.total;
    });

    const loading = computed(() => {
        return articlesLoading.value || dataConfigLoading.value;
    });

    const error = computed(() => {
        return dataConfigError.value || articlesError.value;
    });

    const columns = computed(() => {
        if (!dataDisplayResult.value || dataDisplayResult.value.dataDisplayFields.length === 0) {
            //if no datadisplayfields configured, we need an empty column so that the rows are shown
            return [{ name: "", label: "", field: "", align: "left" }];
        }

        const result = dataDisplayResult.value.dataDisplayFields.map((field) => {
            return {
                name: field.key,
                label: field.title,
                field: field.key,
                align: "left",
            };
        });

        return [
            {
                name: QIT_PREVIEW_IMAGE_COLUMN,
                field: QIT_PREVIEW_IMAGE_ROW_FIELD,
                label: "",
            },
            ...result,
            // TODO - check, if (hasAccess.value) is needed, because the column is always added
            { name: ADD_TO_COLLECTION_COL_NAME, label: "", field: "", align: "left" },
        ];
    });

    const mobileColumns = computed(() => {
        let columns = priorityDataDisplayFields.value.map((displayField) => {
            return {
                name: displayField.key,
                field: displayField.key,
                label: displayField.title,
                align: "left",
            };
        });

        if (hasAccess.value)
            columns = [...columns, { name: ADD_TO_COLLECTION_COL_NAME, label: "", field: "", align: "left" }];

        return columns;
    });

    const dataRows = computed<QTableRowType[]>(() => {
        return articles.value
            ?.filter((edge: ArticleEdge) => edge.node)
            .map((edge: ArticleEdge) => {
                //always add id and previewImage as string field as in QTableRowType
                const result: Partial<QTableRowType> = {
                    id: edge.node.id,
                    [QIT_PREVIEW_IMAGE_ROW_FIELD]: edge.node.previewImage?.url,
                };
                dataDisplayResult.value?.dataDisplayFields
                    .filter((field) => field.key !== "id")
                    .forEach((dataDisplayField) => {
                        const dataValue = getDataDisplayValue(dataDisplayField, edge.node);
                        const isLink = isLinkDisplayField(
                            dataDisplayField.fieldTypeInfo?.fieldType,
                            dataValue.formattedValue
                        );
                        // Store as object with formattedValue and optional symbols
                        result[dataDisplayField.key] = {
                            formattedValue: dataValue.formattedValue,
                            symbols: dataValue.symbols,
                            isLink,
                            colorClass: dataValue.colorClass,
                        };
                    });
                //always add id and previewImage as string field as in QTableRowType
                return result as QTableRowType;
            });
    });

    const qDataSource: ComputedRef<QDataSource> = computed(() => {
        return {
            columns: columns.value as QTableProps["columns"],
            mobileColumns: mobileColumns.value as QTableProps["columns"],
            rows: dataRows.value,
        };
    });

    const resultListEmpty = computed(() => {
        return dataRows.value.length === 0;
    });

    const fetchAggregation = async (searchPhrase: string) => {
        if (!facet.value) return {};

        //remove own facet from selected
        const selectedCopy = [...selected.value.filter((x) => x.referencedId !== facet.value?.referencedId)];
        const filter = generateArticleFilterVariables(searchContext.value, selectedCopy, sort.value, undefined);

        const aggregationResult = await consumerClient.query({
            query: generateDynamicSearchArticlesOnlyAggregationsQuery(facet.value, searchPhrase),
            variables: filter,
        });

        const convertedAggregations = convertAggregationsResult(
            facet.value,
            aggregationResult.data["articles"].aggregations
        );

        return convertedAggregations[facet.value.id];
    };

    return {
        loading,
        error,
        qDataSource,
        resultListEmpty,
        hasMoreResults,
        loadMoreArticles,
        total,
        fetchAggregation,
        articlesResult,
    };
};
